import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';

type Props = {
	statistic: number | string;
	cardContent: MessageDescriptor;
};

export const Card = ({ cardContent, statistic }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<ContentWrapper>
			{formatMessage(cardContent, {
				statistic,
				description: (chunks: ReactNode[]) => <Description>{chunks}</Description>,
				header: (chunks: ReactNode[]) => <Header>{chunks}</Header>,
				stats: (chunks: ReactNode[]) => <Statistic>{chunks}</Statistic>,
			})}
		</ContentWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.p({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	borderRadius: '8px',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N30)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('elevation.surface', colors.N0)}`,
	marginTop: token('space.0', '0px'),
	padding: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		marginRight: token('space.050', '4px'),
	},
	overflowX: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Statistic = styled.span({
	fontWeight: '500',
	fontSize: '24px',
	lineHeight: '28px',
	width: '100%',
	overflowX: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.span({
	display: 'block',
	fontWeight: '600',
	fontSize: '12px',
	lineHeight: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	marginTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	display: 'block',
	marginTop: token('space.050', '4px'),
	fontWeight: '400',
	fontSize: '11px',
	lineHeight: '14px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
});
